import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { isShippingAddressIncluded } from '../../../../../utils/isShippingAddressIncluded'
import { useCustomerForm } from '../../../contexts/customerFormContext'
import { useUpdateDependentCustomerFormValues } from '../../../hooks/useUpdateDependentCustomerFormValues'
import { isShippingAddressSynced } from '../utils/isShippingAddressSynced'

export const useShippingAddress = () => {
  const [isHidden, setIsHidden] = useState(true)
  const [isEditable, setIsEditable] = useState(true)

  const { handleAddressSync } = useUpdateDependentCustomerFormValues()
  const { customerForm } = useCustomerForm()
  const currentAddress = customerForm.watch('address')
  const currentShippingAddress = customerForm.watch('shippingAddress')
  const customerId = customerForm.getValues()?.id

  const handleAddShippingAddress = useCallback(() => {
    setIsHidden(false)
  }, [])

  const handleRemoveShippingAddress = useCallback(() => {
    handleAddressSync(undefined)
    setIsHidden(true)
    setIsEditable(true)
  }, [handleAddressSync])

  const toggleSyncAddress = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIsEditable(!event.target.checked)
  }, [])

  const isIncludedShippingAddress = useMemo(
    () => isShippingAddressIncluded(currentShippingAddress),
    [currentShippingAddress],
  )

  useEffect(() => {
    // Tick "Same as postal address" checkbox if addresses are synced on CustomerModal edit mode open
    if (customerId && isShippingAddressSynced(currentShippingAddress, currentAddress)) {
      setIsEditable(false)
    }
  }, [currentAddress, currentShippingAddress, customerId])

  useEffect(() => {
    if (isHidden && isIncludedShippingAddress) {
      setIsHidden(false)
    }
  }, [isHidden, isIncludedShippingAddress, setIsHidden])

  useEffect(() => {
    if (!isHidden && !isEditable) {
      handleAddressSync(currentAddress)
    } else if (!isHidden && isIncludedShippingAddress) {
      handleAddressSync(currentShippingAddress)
    }
  }, [
    currentAddress,
    currentAddress?.city,
    currentAddress?.country,
    currentAddress?.pobox,
    currentAddress?.streetAddress1,
    currentAddress?.streetAddress2,
    currentShippingAddress,
    handleAddressSync,
    isEditable,
    isHidden,
    isIncludedShippingAddress,
  ])

  return {
    isHidden,
    isEditable,
    handleAddShippingAddress,
    handleRemoveShippingAddress,
    toggleSyncAddress,
  }
}
